import { useMutation, useQuery, useQueryClient } from 'react-query';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Os nomes acompanham as colunas de agencias no IFLEX. Dígito e Posto se
// alternam conforme o Banco: o IFLEX usa Posto no Sicredi e Dígito nos demais,
// e grava zero no que não vem preenchido — esse zero é preenchimento, não faz
// parte da numeração.
export interface Agencia {
  id_agc: string;
  banco_agc: string | null;
  agencia_agc: string;
  digito_agc: string | null;
  posto_agc: string | null;
  descricao_agc: string | null;
  gerente_agc: string | null;
  telefone_agc: string | null;
  nome_ban: string | null;
  // Opcionais: contato e endereço da Agência, as mesmas colunas que a tela
  // do IFLEX preenche.
  telefone2_agc: string | null;
  cnpj_agc: string | null;
  cep_agc: string | null;
  rua_agc: string | null;
  numero_agc: string | null;
  complemento_agc: string | null;
  bairro_agc: string | null;
  municipio_agc: string | null;
  nome_mun: string | null;
  sigla_est: string | null;
}

export interface DadosAgencia {
  banco: string;
  agencia: string;
  digito: string;
  posto: string;
  descricao: string;
  gerente: string;
  telefone: string;
  telefone2: string;
  cnpj: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
}

export interface Municipio {
  id_mun: string;
  nome_mun: string;
  sigla_est: string | null;
}

// Como o Município aparece na lista e no campo: o nome sozinho se repete
// entre estados — há uma Bom Jesus em sete deles.
export function rotuloMunicipio(m: {
  nome_mun: string | null;
  sigla_est: string | null;
}): string {
  if (!m.nome_mun) return '';
  return m.sigla_est ? `${m.nome_mun}/${m.sigla_est}` : m.nome_mun;
}

/**
 * Municípios que casam com o trecho digitado.
 *
 * São mais de cinco mil: quem filtra é o servidor, que devolve no máximo
 * trinta. `atual` traz junto o que já está gravado, para o campo mostrar o
 * Município da Agência ao reabrir o cadastro sem nada digitado.
 */
export function useMunicipios(busca: string, atual = '') {
  const termo = busca.trim();
  return useQuery(
    ['oktonbank-municipios', termo, atual],
    async () => {
      const dados = await chamarOktonBank<{ rows: Municipio[] }>(
        'OBContas',
        'listarMunicipios',
        { busca: termo, atual },
      );
      return Array.isArray(dados?.rows) ? dados.rows : [];
    },
    // Sem trecho nem Município gravado a busca devolveria os trinta
    // primeiros do alfabeto, que não ajudam ninguém.
    { enabled: termo.length >= 2 || atual !== '', staleTime: 1000 * 60 },
  );
}

export function useAgenciasCadastro() {
  return useQuery(['oktonbank-agencias-cadastro'], async () => {
    const dados = await chamarOktonBank<{ rows: Agencia[] }>(
      'OBContas',
      'listarAgenciasCadastro',
    );
    return Array.isArray(dados?.rows) ? dados.rows : [];
  });
}

// A Conta mostra a Agência no formulário e na grade, então as duas listas
// envelhecem juntas a cada gravação.
function invalidarAgencias(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries('oktonbank-agencias-cadastro');
  queryClient.invalidateQueries('oktonbank-agencias');
  queryClient.invalidateQueries('contas-bancarias');
}

export function useSaveAgencia() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, ...payload }: DadosAgencia & { id?: string }) =>
      enviarOktonBank('OBContas', 'gravarAgencia', {
        id: id ?? '',
        ...payload,
      }),
    { onSuccess: () => invalidarAgencias(queryClient) },
  );
}

export function useDeleteAgencia() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => enviarOktonBank('OBContas', 'removerAgencia', { id }),
    { onSuccess: () => invalidarAgencias(queryClient) },
  );
}
